import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MdOutlineCancel } from 'react-icons/md';
import { useStateContext } from '../../../contexts/ContextProvider';
import { Button } from '../../../components';
import FormInput from '../../../components/FormField';
import { updateDocument, config, uploadData } from '../../../lib/appwrite';
import DropdownField from '../../../components/DropdownField';

const EditUser = ({ view, setView, selectedRowData, setSelectedRowData, mode, onSuccess }) => {
  const { currentColor } = useStateContext();
  const isMountedRef = useRef(true);
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';

  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [permissions, setPermissions] = useState('user');
  const [isLoading, setIsLoading] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;

    if (view && selectedRowData && mode === 'edit') {
      setUsername(selectedRowData.username || '');
      setEmail(selectedRowData.email || '');
      setPermissions(selectedRowData.permissions || 'user');
      setPassword('');
      setConfirmPassword('');
      setResetPassword(false);
    } else if (view && mode === 'create') {
      // Reset form for create mode
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setPermissions('user');
      setResetPassword(false);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [view, selectedRowData, mode]);

  const close_menu = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setPermissions('user');
    setUsernameError(false);
    setEmailError(false);
    setPasswordError(false);
    setConfirmPasswordError(false);
    setResetPassword(false);
    setSelectedRowData(null);
    setView(false);
    setIsLoading(false);
  };

  const submit = async () => {
    // Reset errors
    setUsernameError(false);
    setEmailError(false);
    setPasswordError(false);
    setConfirmPasswordError(false);

    // Validate
    let hasError = false;
    if (username === '') {
      setUsernameError(true);
      hasError = true;
    }
    if (email === '') {
      setEmailError(true);
      hasError = true;
    }

    // Password is required for create mode
    if (mode === 'create' && !password) {
      setPasswordError(true);
      hasError = true;
    }

    // If resetting password in edit mode, password is required
    if (mode === 'edit' && resetPassword && !password) {
      setPasswordError(true);
      hasError = true;
    }

    // Check password confirmation
    if (password && password !== confirmPassword) {
      setConfirmPasswordError(true);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setIsLoading(true);

      const token = localStorage.getItem('auth_token');

      if (mode === 'edit') {
        // Update user
        const updateData = {
          username,
          email,
          permissions,
        };

        await updateDocument(selectedRowData.$id || selectedRowData.id, updateData, config.userCollectionId);

        // If resetting password, call the reset password endpoint
        if (resetPassword && password) {
          const resetResponse = await fetch(`${API_BASE_URL}/api/users/${selectedRowData.id || selectedRowData.$id}/reset-password`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ newPassword: password }),
          });

          if (!resetResponse.ok) {
            const errorData = await resetResponse.json();
            throw new Error(errorData.error || 'Failed to reset password');
          }
        }
      } else {
        // Create new user
        const userData = {
          username,
          email,
          password,
          permissions,
        };

        const response = await fetch(`${API_BASE_URL}/api/users/signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(userData),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create user');
        }
      }

      if (!isMountedRef.current) return;

      if (onSuccess) {
        onSuccess();
      }
      close_menu();
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error saving user:', error);
      alert(error.message || 'Failed to save user. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  if (!view) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={close_menu}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              {mode === 'create' ? 'Add New User' : 'Edit User'}
            </h2>
            <button
              onClick={close_menu}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <MdOutlineCancel className="text-2xl" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <FormInput
              title="Username"
              placeholder="Enter username"
              value={username}
              handleChangeText={setUsername}
              error={usernameError}
              required
            />

            <FormInput
              title="Email"
              placeholder="Enter email"
              value={email}
              handleChangeText={setEmail}
              error={emailError}
              required
            />

            <DropdownField
              selected={permissions}
              setSelected={setPermissions}
              title="Permissions"
              options={['admin', 'user']}
              handleChangeText={setPermissions}
              required
            />

            {mode === 'create' && (
              <>
                <FormInput
                  title="Password"
                  placeholder="Enter password"
                  value={password}
                  handleChangeText={setPassword}
                  type="password"
                  error={passwordError}
                  required
                />

                <FormInput
                  title="Confirm Password"
                  placeholder="Confirm password"
                  value={confirmPassword}
                  handleChangeText={setConfirmPassword}
                  type="password"
                  error={confirmPasswordError}
                  required
                />
              </>
            )}

            {mode === 'edit' && (
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="resetPassword"
                    checked={resetPassword}
                    onChange={(e) => setResetPassword(e.target.checked)}
                    className="mr-2"
                  />
                  <label htmlFor="resetPassword" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Reset password (forgot password)
                  </label>
                </div>

                {resetPassword && (
                  <>
                    <FormInput
                      title="New Password"
                      placeholder="Enter new password"
                      value={password}
                      handleChangeText={setPassword}
                      type="password"
                      error={passwordError}
                      required
                    />

                    <FormInput
                      title="Confirm New Password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      handleChangeText={setConfirmPassword}
                      type="password"
                      error={confirmPasswordError}
                      required
                    />
                  </>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={close_menu}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <Button
                bgColor={currentColor || '#1E4DB7'}
                text={mode === 'create' ? 'Create User' : 'Save Changes'}
                borderRadius="8px"
                color="white"
                isLoading={isLoading}
                onClick={submit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EditUser;

